import { ArrowRightAlt } from '@mui/icons-material';
import { Box } from '@mui/material';
import { Difference } from 'microdiff';
import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AuditTargetType,
  AuditType,
} from '~/data/userActivity/useGetAuditSummary';
import {
  UserActivityChangeEventsRow,
  UserActivityLogEventDataPoint,
} from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/data';
import { useSxStyles } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/EmployeeChangeActivityDataContent/useStyles';
import { mergeSxProps } from '~/utils/helpers/sxProps';

type LogEntryDataPoint = {
  label: string;
  value?: string | number | ReactNode;
  differenceType?: Difference['type'];
};

const LogEntryDataInfo = (
  props: LogEntryDataPoint & {
    showSeparator?: boolean;
    secondaryLabel?: boolean;
    children?: ReactNode;
  },
) => {
  const {
    label,
    value,
    showSeparator,
    differenceType,
    secondaryLabel,
    children,
  } = props;

  const sxStyles = useSxStyles();

  const contentSxStyles = useMemo(() => {
    const getDifferenceColor = () => {
      switch (differenceType) {
        case 'CREATE':
          return sxStyles.diffCreate;
        case 'CHANGE':
          return sxStyles.diffChange;
        case 'REMOVE':
          return sxStyles.diffRemove;
        default:
          return undefined;
      }
    };

    return {
      root: secondaryLabel ? sxStyles.secondaryPoint : undefined,
      label: secondaryLabel ? sxStyles.secondaryLabel : undefined,
      value: getDifferenceColor(),
    };
  }, [sxStyles, differenceType, secondaryLabel]);

  return (
    <Box sx={mergeSxProps(sxStyles.dataPoint, contentSxStyles.root)}>
      <Box sx={mergeSxProps(sxStyles.dataPointLabel, contentSxStyles.label)}>
        {label}:
      </Box>
      {!!children ? (
        <Box sx={contentSxStyles.value}>{children}</Box>
      ) : (
        <Box sx={contentSxStyles.value}>
          {value}
          {showSeparator && ';'}
        </Box>
      )}
    </Box>
  );
};

const DataChanges = (props: {
  changes?: Difference[];
  dataPointChanges?: UserActivityLogEventDataPoint[];
}) => {
  const { changes, dataPointChanges } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  if (!changes && !dataPointChanges) {
    return <></>;
  }

  return (
    <LogEntryDataInfo label={t('changes')}>
      {dataPointChanges?.map((dataChange, index) => {
        const { label, value, differenceType } = dataChange;

        const getValue = () => {
          if (typeof value === 'string' && !!differenceType) {
            const [oldValue, newValue] = value.split('=>');

            return (
              <>
                {oldValue}
                <ArrowRightAlt sx={sxStyles.changeArrow} />
                {newValue}
              </>
            );
          }

          return value;
        };

        return (
          <LogEntryDataInfo
            key={`diff-result-${index}`}
            label={label}
            value={getValue()}
            differenceType={differenceType}
            secondaryLabel={true}
          />
        );
      })}
      {changes?.map((dataChange, index) => {
        const label = dataChange.path.join('.');

        const getValue = () => {
          switch (dataChange.type) {
            case 'CHANGE': {
              return (
                <>
                  {dataChange.oldValue}{' '}
                  <ArrowRightAlt sx={sxStyles.changeArrow} /> {dataChange.value}
                </>
              );
            }
            case 'CREATE': {
              return `=> ${dataChange.value}`;
            }
            case 'REMOVE': {
              return `${dataChange.oldValue} => `;
            }
            default:
              return undefined;
          }
        };

        return (
          <LogEntryDataInfo
            key={`diff-result-${index}`}
            label={label}
            value={getValue()}
            differenceType={dataChange.type}
            secondaryLabel={true}
          />
        );
      })}
    </LogEntryDataInfo>
  );
};

const DataListValues = (props: { values: UserActivityLogEventDataPoint[] }) => {
  const { values } = props;

  const isLastInArray = (arrayLength: number) => (index: number) =>
    arrayLength === index + 1;
  const isLast = isLastInArray(values.length);

  return (
    <>
      {values.map(({ key: _key, ...valueProps }, index) => {
        return (
          <LogEntryDataInfo
            key={`data-point-${index}`}
            {...valueProps}
            showSeparator={!isLast(index)}
          />
        );
      })}
    </>
  );
};

export const EmployeeChangeActivityDataContent = (props: {
  row: UserActivityChangeEventsRow;
  isPanel?: boolean;
}): ReactElement => {
  const { row, isPanel } = props;

  const renderContent = useCallback(
    (content: { changes?: ReactNode; data?: ReactNode }): ReactElement => {
      if (isPanel) {
        return (
          <Box>
            <Box>{content.changes}</Box>
            <Box>{content.data}</Box>
          </Box>
        );
      }

      return <>{content.changes ? content.changes : content.data}</>;
    },
    [isPanel],
  );

  const returnDefault = () => {
    return renderContent({
      changes:
        !!row.dataPointChanges && row.dataPointChanges.length ? (
          <DataChanges dataPointChanges={row.dataPointChanges} />
        ) : undefined,
      data: <DataListValues values={row.dataPoints} />,
    });
  };

  switch (row.targetType) {
    case AuditTargetType.Entity: {
      return returnDefault();
    }
    case AuditTargetType.Employee: {
      switch (row.auditType) {
        case AuditType.AuthUser:
        case AuditType.AuthUserRole:
        case AuditType.AuthResponsibility:
        case AuditType.AbsenceShort:
        case AuditType.AbsenceLong: {
          return returnDefault();
        }
        default:
          return returnDefault();
      }
    }
    default:
      return returnDefault();
  }
};
